import React from 'react';
import { useTranslation } from 'react-i18next';

import type Location from '@r6digital/rapidstor-types/react/entities/Location';

import useRapidStorTracking from '@/core/hooks/useRapidStorTracking';
import classes from '@/standard-unit-listing/components/elements/quick-size-filter/styles.module.scss';

import type { SizeFilter } from '../../../sdk/listing';
import { useListingSettings } from '../../../sdk/useSite';

interface QuickSizeFilterProps {
    value: SizeFilter;
    onChange: (value: SizeFilter) => void;
    location: Location;
}

/**
 * The quick size filter above the listing grid. Which options appear is driven by the site's
 * listing settings.
 *
 * Takes `location` as a prop rather than reading `useLoaderData()`, so the component stays testable
 * outside a router — the SDK's own "inject, don't read globals" convention.
 */
function QuickSizeFilter({ value, onChange, location }: QuickSizeFilterProps) {
    const { t } = useTranslation();
    const { trackEvent } = useRapidStorTracking();
    const settings = useListingSettings(location);

    const options: { label: string; value: SizeFilter }[] = [
        { label: t('standardUnitListing:all'), value: 'all' },
        { label: t('standardUnitListing:small'), value: 'small' },
        { label: t('standardUnitListing:medium'), value: 'medium' },
        { label: t('standardUnitListing:large'), value: 'large' },
        { label: t('standardUnitListing:vehicle'), value: 'vehicle' },
    ];

    return (
        <div className={classes.wrapper} role="radiogroup" aria-label="Quick Search">
            <span className={classes.label}>{t('standardUnitListing:quickSearch')}</span>
            <div className={classes.options}>
                {options
                    .filter((o) => o.value !== 'vehicle' || settings.showVehicleQuickSearch())
                    .map((o) => (
                        <button
                            key={o.value}
                            type="button"
                            role="radio"
                            aria-checked={value === o.value}
                            className={value === o.value ? classes.active : classes.inactive}
                            onClick={() => {
                                onChange(o.value);
                                trackEvent('size_filter_clicked', { filter: o.value });
                            }}
                        >
                            {o.label}
                        </button>
                    ))}
            </div>
        </div>
    );
}

export default QuickSizeFilter;
